import { Injectable, signal, inject } from '@angular/core';
import { BehaviorSubject, catchError, Observable, of, tap } from 'rxjs';
import { APIConstants } from '../constants/api.constant';
import { CommonApiFuncService, StorageService, StorageType } from '@anddone/ui-utilities';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class Notification {
  loggedInUserData: any = {};
  private selectedNotificationRow = new BehaviorSubject<boolean>(false);
  emailAdded = signal(0);
  emailUpdated = signal(0);
  private http = inject(HttpClient);
  private storageService = inject(StorageService);
  private commonAPIFuncService = inject(CommonApiFuncService);
  // Mock Data start
  getReportTemplateMock(): Observable<[]> {
    return this.http.get<any>('/assets/mock-data/notification-template-data.json');
  }

  getLoggedInData() {
    return JSON.parse(this.storageService.get(StorageType.Session, 'userDetails') ?? '{}');
  }

  // get all notification list
  getAllNotifications(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    if (!this.loggedInUserData) {
      return of(null);
    }
    let url = APIConstants.notifications.getAllNotifications.replace(
      '{allyId}',
      this.loggedInUserData.id
    );
    url = `${url}${this.commonAPIFuncService.buildQuery(reqObj)}`;
    const options = {
      customBaseUrl: environment.infraBaseUrl,
    };
    return this.commonAPIFuncService.get(url, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`getAllNotifications success`)),
      catchError(this.commonAPIFuncService.handleError('getAllNotifications error'))
    );
  }

  // Pause/Unpause all  notification
  pauseUnpauseAllNotification(data: any) {
    this.loggedInUserData = this.getLoggedInData();
    if (!this.loggedInUserData) {
      return of(null);
    }
    const url = APIConstants.notifications.pauseunpauseAll.replace(
      '{allyId}',
      this.loggedInUserData.id
    );
    const options = {
      customBaseUrl: environment.infraBaseUrl,
    };
    return this.commonAPIFuncService.put(url, data, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`pauseUnpauseAllNotification success`)),
      catchError(this.commonAPIFuncService.handleError('pauseUnpauseAllNotification error'))
    );
  }

  subscribeDefaultNotification(id: string) {
    const url = APIConstants.notifications.defaultSubscribe
      .replace('{allyId}', this.loggedInUserData.id)
      .replace('{id}', id);
    const options = {
      customBaseUrl: environment.infraBaseUrl,
    };
    return this.commonAPIFuncService.post(url, null, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`subscribeDefaultNotification success`)),
      catchError(this.commonAPIFuncService.handleError('subscribeDefaultNotification error'))
    );
  }

  subscribeAllyNotification(id: string, data: any) {
    const url = APIConstants.notifications.alliesSubscribe
      .replace('{allyId}', this.loggedInUserData.id)
      .replace('{id}', id);
    const options = {
      customBaseUrl: environment.infraBaseUrl,
    };
    return this.commonAPIFuncService.put(url, data, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`subscribeAllyNotification success`)),
      catchError(this.commonAPIFuncService.handleError('subscribeAllyNotification error'))
    );
  }

  addDistributionList(notificationId: string, obj: any) {
    this.loggedInUserData = this.getLoggedInData();
    if (!this.loggedInUserData) {
      return of(null);
    }
    const url = APIConstants.notifications.addDistributionEmail
      .replace('{allyId}', this.loggedInUserData.id)
      .replace('{notificationId}', notificationId);
    const options = {
      customBaseUrl: environment.infraBaseUrl,
    };
    return this.commonAPIFuncService.post(url, obj, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`addDistributionList success`)),
      catchError(this.commonAPIFuncService.handleError('addDistributionList error'))
    );
  }

  getNotificationEventDetails(allyId: string, notificationId: string) {
    const url = APIConstants.notifications.getEventDetails
      .replace('{allyId}', allyId)
      .replace('{notificationId}', notificationId);
    const options = {
      customBaseUrl: environment.infraBaseUrl,
    };
    return this.commonAPIFuncService.get(url, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`getNotificationEventDetails success`)),
      catchError(this.commonAPIFuncService.handleError('getNotificationEventDetails error'))
    );
  }

  getDistributionListWithPagination(reqObj: any, notificationId: string, distributionId: string) {
    this.loggedInUserData = this.getLoggedInData();
    if (!this.loggedInUserData) {
      return of(null);
    }
    let url = APIConstants.notifications.getDistributionListWithPagination
      .replace('{allyId}', this.loggedInUserData.id)
      .replace('{notificationId}', notificationId)
      .replace('{distributionId}', distributionId);
    url = `${url}${this.commonAPIFuncService.buildQuery(reqObj)}`;
    const options = {
      customBaseUrl: environment.infraBaseUrl,
    };
    return this.commonAPIFuncService.get(url, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`getDistributionListWithPagination success`)),
      catchError(this.commonAPIFuncService.handleError('getDistributionListWithPagination error'))
    );
  }

  updateDistributionList(notificationId: string, distributionId: string, obj: any) {
    this.loggedInUserData = this.getLoggedInData();
    if (!this.loggedInUserData) {
      return of(null);
    }
    const url = APIConstants.notifications.updateDistributionEmail
      .replace('{allyId}', this.loggedInUserData.id)
      .replace('{notificationId}', notificationId)
      .replace('{distributionId}', distributionId);
    const options = {
      customBaseUrl: environment.infraBaseUrl,
    };
    return this.commonAPIFuncService.put(url, obj, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`updateDistributionList success`)),
      catchError(this.commonAPIFuncService.handleError('updateDistributionList error'))
    );
  }

  deleteDistributionEmail(obj: any, notificationId: string, distributionId: string) {
    this.loggedInUserData = this.getLoggedInData();
    if (!this.loggedInUserData) {
      return of(null);
    }
    let url = APIConstants.notifications.deleteDistributionEmail
      .replace('{allyId}', this.loggedInUserData.id)
      .replace('{notificationId}', notificationId)
      .replace('{distributionId}', distributionId);
    url = `${url}${this.commonAPIFuncService.buildQuery(obj)}`;
    const options = {
      customBaseUrl: environment.infraBaseUrl,
    };
    return this.commonAPIFuncService.delete(url, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`deleteDistributionEmail success`)),
      catchError(this.commonAPIFuncService.handleError('deleteDistributionEmail error'))
    );
  }

  getNotificationRow() {
    return this.selectedNotificationRow.asObservable();
  }

  // Setter for the key property
  setNotificationRow(value: any) {
    this.selectedNotificationRow.next(value);
  }

  setEmailAdded(): void {
    this.emailAdded.update((value) => value + 1);
  }

  setEmailUpdated(): void {
    this.emailUpdated.update((value) => value + 1);
  }

  updateNotificationTemplate(obj: any) {
    const loggedInUserData = this.getLoggedInData();

    let url, urlType;

    const replaceParams = {
      allyId: loggedInUserData.id,
      notificationId: obj.notificationId,
      templateId: obj.templateId,
    };
    const options = {
      customBaseUrl: environment.infraBaseUrl,
    };

    if (obj.type === 'POST-API-Call') {
      url = APIConstants.notifications.editPostNotificationTemplate
        .replace('{allyId}', replaceParams.allyId)
        .replace('{notificationId}', replaceParams.notificationId);
      urlType = this.commonAPIFuncService.post(url, obj.data, options);
    } else if (obj.type === 'PUT-API-Call') {
      url = APIConstants.notifications.editPutNotificationTemplate
        .replace('{allyId}', replaceParams.allyId)
        .replace('{notificationId}', replaceParams.notificationId)
        .replace('{templateId}', replaceParams.templateId);

      urlType = this.commonAPIFuncService.put(url, obj.data, options);
    }

    return urlType?.pipe(
      tap((_a) => this.commonAPIFuncService.log(`updateNotificationTemplate success`)),
      catchError(this.commonAPIFuncService.handleError('updateNotificationTemplate error'))
    );
  }

  getEventVariables(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    if (!this.loggedInUserData) {
      return of(null);
    }
    let url = APIConstants.notifications.getEventVariables.replace(
      '{allyId}',
      this.loggedInUserData.id
    );
    url = `${url}${this.commonAPIFuncService.buildQuery(reqObj)}`;
    const options = {
      customBaseUrl: environment.infraBaseUrl,
    };
    return this.commonAPIFuncService.get(url, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`getEventVariables success`)),
      catchError(this.commonAPIFuncService.handleError('getEventVariables error'))
    );
  }

  resetToSystemDefaultTemplate(notificationId: string) {
    this.loggedInUserData = this.getLoggedInData();
    if (!this.loggedInUserData) {
      return of(null);
    }
    const url = APIConstants.notifications.resetToSystemDefault
      .replace('{allyId}', this.loggedInUserData.id)
      .replace('{notificationId}', notificationId);
    const options = {
      customBaseUrl: environment.infraBaseUrl,
    };
    return this.commonAPIFuncService.delete(url, options).pipe(
      tap((_) => this.commonAPIFuncService.log(`resetToSystemDefaultTemplate success`)),
      catchError(this.commonAPIFuncService.handleError('resetToSystemDefaultTemplate error'))
    );
  }
}
